import { APICurrentUser } from '@ardoq/api-types';
import type { FeatureContext } from '@ardoq/features';
import type { TourId } from '@ardoq/product-tours';
import { CarouselConfigurableProps, CarouselCard } from './carousel/Carousel';
import { InteractiveNotificationConfig } from '@ardoq/status-ui';
import { I18nLocale } from '@ardoq/i18n';
import { PopoverPlacement } from '@ardoq/popovers';
import {
  HeroBannerBackground,
  HeroBannerConfig,
} from './heroBannerNotification/HeroBanner';

/** Rename-locked: the ephemeral-notification cleanup job matches this name. */
export enum EphemeralNotificationKeys {
  PLACE_HOLDER = 'place-holder-key',

  COMPONENT_MATRIX_IN_VIEWPOINT_MODE = 'component-matrix-in-viewpoint-mode',
  CONDITIONAL_FORMATTING_AI_ONBOARDING = 'conditional-formatting-ai-onboarding',
  LABELS_AI_ONBOARDING = 'labels-ai-onboarding',
  DISCOVER_EXPERIENCE_TOUR = 'discover-experience-tour',
  DATA_INVENTORY_2026_10_UPDATES_WITH_TOUR = 'data-inventory-2026-10-updates-with-tour',
}

export type EphemeralNotificationRelevanceContext = {
  featureContext: FeatureContext;
  toursWithSatisfiedDataGates: readonly TourId[];
};

export type EphemeralExternalLink = {
  text: string;
  url: string;
};

/** Flat properties added to every `EphNotif:` event of one notification, e.g. `{ variant }`. */
export type EphemeralEventProperties = Record<string, string>;

/** `views` counts prior views, `hours` counts from the first view. */
export type EphemeralResolveDelay = { views: number } | { hours: number };

export type EphemeralNotificationLifecycle = {
  /** Row value in the tracking table; must be unique. */
  id: EphemeralNotificationKeys;
  /**
   * Visible between these dates, inclusive. `relevantUntilDate` is
   * rename-locked: set it as a literal `parseDate('YYYY-MM-DD')`, or omit it
   * for no expiry.
   */
  relevantFromDate: Date;
  relevantUntilDate?: Date;
  /** Whether the notification applies to the current user. */
  isRelevantForCurrentUser: (
    currentUser: APICurrentUser,
    relevanceContext: EphemeralNotificationRelevanceContext
  ) => boolean;
  /** Per-user exposure window: cutoff is `min(relevantUntilDate, firstSeenAt + personalWindowDays)`. */
  personalWindowDays?: number;
  /** Hides the notification after `maxViews` counted views (see `ephemeralNotificationsStateOperations.ts`). */
  maxViews?: number;
  /**
   * Only visible once the referenced notification was resolved in a previous
   * session. Ignored once the reference can no longer be resolved.
   */
  dependsOnResolved?: EphemeralNotificationKeys;
};

type WithLifecycle<TContent, TKind extends string> = TContent &
  EphemeralNotificationLifecycle & { kind: TKind };

/** The interaction behaviour every banner shape shares. */
export type EphemeralBannerInteractionBase = {
  onActionButtonClick?: VoidFunction;
  /**
   * `false` hides the close button and stops interactions from recording a
   * resolution. Evaluated on render. Defaults to resolvable.
   */
  isResolvable?: () => boolean;
  /**
   * Delays every resolving interaction. Frozen per mount, so `{ views: N }`
   * unlocks on the page load after the Nth counted view and `{ hours: N }` on
   * the first load past the deadline.
   */
  resolvableAfter?: EphemeralResolveDelay;
  shouldResolveOnExternalLinkClick?: false;
  eventProperties?: EphemeralEventProperties;
};

export type EphemeralBannerDisplayConfig = Omit<
  InteractiveNotificationConfig,
  'externalLink'
> & {
  externalLink?: EphemeralExternalLink;
};

export type EphemeralBannerNotificationContent = EphemeralBannerDisplayConfig &
  EphemeralBannerInteractionBase;

export type EphemeralBannerNotificationConfig = WithLifecycle<
  EphemeralBannerNotificationContent,
  'banner'
>;

export type EphemeralHeroBannerNotificationContent = HeroBannerConfig &
  EphemeralBannerInteractionBase & {
    background: HeroBannerBackground;
  };

export type EphemeralHeroBannerNotificationConfig = WithLifecycle<
  EphemeralHeroBannerNotificationContent,
  'heroBanner'
>;

export type EphemeralBannerConfig =
  | EphemeralBannerNotificationConfig
  | EphemeralHeroBannerNotificationConfig;

export type EphemeralAnchor = {
  /** CSS selector for the element to anchor the callout to. */
  target: string;
  /** Preferred side of the target; flips to fit the viewport. Defaults to top/center. */
  placement?: PopoverPlacement;
  /** Highlight the target with a pulsing ring. Defaults to true. */
  hasSpotlight?: boolean;
  /** Dim and block interaction with the rest of the page. Defaults to true. */
  hasBackgroundDim?: boolean;
};

/** The content configuration every carousel shape shares. */
export type EphemeralCarouselContentBase = CarouselConfigurableProps & {
  onActionButtonClick?: VoidFunction;
  eventProperties?: EphemeralEventProperties;
};

/** Popover and corner fix the carousel's size, shadow and close button. */
type EphemeralSingleCardContentBase = Pick<
  EphemeralCarouselContentBase,
  | 'showSkipButton'
  | 'skipButtonLabel'
  | 'endButtonLabel'
  | 'onActionButtonClick'
  | 'eventProperties'
> & {
  cards: [CarouselCard];
};

export type EphemeralModalNotificationContent = EphemeralCarouselContentBase;

export type EphemeralModalNotificationConfig = WithLifecycle<
  EphemeralModalNotificationContent,
  'modal'
>;

export type EphemeralPopoverNotificationContent =
  EphemeralSingleCardContentBase & {
    anchor: EphemeralAnchor;
  };

export type EphemeralPopoverNotificationConfig = WithLifecycle<
  EphemeralPopoverNotificationContent,
  'popover'
>;

export type EphemeralCornerNotificationContent =
  EphemeralSingleCardContentBase & {
    /** Which viewport corner to float the carousel in. Defaults to `'bottom-left'`. */
    position?: 'bottom-left' | 'bottom-right';
  };

export type EphemeralCornerNotificationConfig = WithLifecycle<
  EphemeralCornerNotificationContent,
  'corner'
>;

export type EphemeralNotificationConfig =
  | EphemeralBannerNotificationConfig
  | EphemeralHeroBannerNotificationConfig
  | EphemeralModalNotificationConfig
  | EphemeralPopoverNotificationConfig
  | EphemeralCornerNotificationConfig;

export type TrackableNotificationConfig = Pick<
  EphemeralNotificationConfig,
  'id' | 'kind' | 'eventProperties'
>;

export type EphemeralNotificationEvent =
  | { type: 'seen' }
  | { type: 'actionClicked'; closedNotification: boolean }
  | { type: 'dismissed' }
  | { type: 'externalLinkClicked'; slideIndex: number | null }
  | {
      type: 'slideNavigated';
      fromSlideIndex: number;
      toSlideIndex: number;
      slideCount: number;
    };

/** Dispatched by the UI; `seen` only comes from a counted view. */
export type EphemeralNotificationInteraction = Exclude<
  EphemeralNotificationEvent,
  { type: 'seen' }
>;

/**
 * `notificationConfig` is `null` while the notification is hidden or the
 * streamed config is not the shape the component renders.
 */
export type EphemeralNotificationStreamedProps<
  TConfig extends EphemeralNotificationConfig,
> = {
  notificationConfig: TConfig | null;
  instanceId: string;
  i18nLocale: I18nLocale;
};

/** Banners need `currentUserState` to resolve `resolvableAfter`; carousels never read it. */
export type EphemeralBannerStreamedProps<
  TConfig extends EphemeralBannerConfig,
> = EphemeralNotificationStreamedProps<TConfig> & {
  currentUserState: APICurrentUser | null;
};
